import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'
import { useClient } from '@/api/queries'
import { useAuth } from '@/auth/useAuth'
import { Card } from '@/components/ui/Card'
import { ErrorState, SkeletonRows } from '@/components/ui/States'
import { Button } from '@/components/ui/Button'

/**
 * Guards are UX, not security. The API enforces tenancy via RLS regardless of
 * what the SPA renders (docs/02-app-shell.md §4).
 */

/** Only validates that `next` is a same-origin relative path — an unvalidated
 *  `next` is an open redirect (docs/03-page-specs.md §1). */
export function safeNext(value: string | null): string {
  if (!value) return '/'
  if (!value.startsWith('/') || value.startsWith('//')) return '/'
  return value
}

export function RequireAuth() {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="p-6">
        <Card>
          <SkeletonRows rows={4} />
        </Card>
      </div>
    )
  }

  if (!user) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`)
    return <Navigate to={`/login?next=${next}`} replace />
  }

  return <Outlet />
}

/**
 * Resolves the client named in the URL. A 403 renders a message rather than
 * bouncing — a silent redirect on a permissions failure hides the bug.
 */
export function RequireClient() {
  const { clientId } = useParams()
  const { isLoading, isError, refetch } = useClient(clientId ?? '')

  if (!clientId) return <Navigate to="/" replace />

  if (isLoading) {
    return (
      <Card>
        <SkeletonRows rows={5} />
      </Card>
    )
  }

  if (isError) {
    return (
      <Card>
        <ErrorState
          title="No access to this client"
          message="This client either does not exist or is not shared with your account. If you expected access, ask an administrator to add you."
          onRetry={() => void refetch()}
        />
        <div className="flex justify-center pb-6">
          <Button size="sm" onClick={() => window.location.assign('/')}>
            Back to all clients
          </Button>
        </div>
      </Card>
    )
  }

  return <Outlet />
}
